import { PoDialogConfig, PO_DIALOG_ICON } from '../purchase-order-detail/purchase-order.constants';

// Company statuses shown in the Status column badge and the Status filter.
// Codes are snake_case to match the CSS badge modifier classes (.fh-status--<code>).
// The service normalizes the backend's UPPER_CASE values (e.g. "UNVERIFIED") to these codes.
export const COMPANY_STATUS = {
  UNVERIFIED:                            'unverified',
  VERIFIED:                              'verified',
  PARTIALLY_VERIFIED:                    'partially_verified',
  AWAITING_COMPLIANCE_VALIDATION:        'awaiting_compliance_validation',
  AWAITING_FINANCIAL_VALIDATION:         'awaiting_financial_validation',
  AWAITING_MANUAL_COMPLIANCE_VALIDATION: 'awaiting_manual_compliance_validation',
  WAITING_FOR_KAM:                       'waiting_for_kam',
  DRAFT:                                 'draft',
  BLOCKED:                               'blocked'
} as const;
export type CompanyStatus = typeof COMPANY_STATUS[keyof typeof COMPANY_STATUS];

// Display metadata for each status code — `label` is shown in the Status column badge and the
// Status filter; `cssClass` is the snake_case code used to build the badge CSS modifier class
// (e.g. `fh-status--<cssClass>` in the table, `cd-status--<cssClass>` in the detail view).
export interface CompanyStatusMeta {
  label: string;
  cssClass: string;
}

export const COMPANY_STATUS_LABELS: Record<CompanyStatus, CompanyStatusMeta> = {
  [COMPANY_STATUS.UNVERIFIED]: {
    label: 'Unverified',                             
    cssClass: 'unverified'
  },
  [COMPANY_STATUS.VERIFIED]: {
    label: 'Verified',                               
    cssClass: 'verified'
  },
  [COMPANY_STATUS.PARTIALLY_VERIFIED]: {
    label: 'Partially Verified',                     
    cssClass: 'partially_verified'
  },
  [COMPANY_STATUS.AWAITING_COMPLIANCE_VALIDATION]: {
    label: 'Awaiting compliance validation',         
    cssClass: 'awaiting_compliance_validation'
  },
  [COMPANY_STATUS.AWAITING_FINANCIAL_VALIDATION]: {
    label: 'Awaiting financial validation',          
    cssClass: 'awaiting_financial_validation'
  },
  [COMPANY_STATUS.AWAITING_MANUAL_COMPLIANCE_VALIDATION]: {
    label: 'Awaiting manual compliance validation',  
    cssClass: 'awaiting_manual_compliance_validation'
  },
  [COMPANY_STATUS.WAITING_FOR_KAM]: {
    label: 'Waiting for KAM',                        
    cssClass: 'waiting_for_kam'
  },
  [COMPANY_STATUS.DRAFT]: {
    label: 'Draft',                                  
    cssClass: 'draft'
  },
  [COMPANY_STATUS.BLOCKED]: {
    label: 'Blocked',                                
    cssClass: 'blocked'
  },
};

// Company types shown in the Company type column and the Company type filter.
export const COMPANY_TYPE = {
  MAKER:           'maker',
  RETAILER:        'retailer',
  MAKER_RETAILER:  'maker_retailer',
  AGENCY:          'agency',
  SERVICE_PROVIDER:'service_provider',
} as const;
export type CompanyType = typeof COMPANY_TYPE[keyof typeof COMPANY_TYPE];

// Display labels for each company type code.
export const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  [COMPANY_TYPE.MAKER]:            'Maker',
  [COMPANY_TYPE.RETAILER]:         'Retailer',
  [COMPANY_TYPE.MAKER_RETAILER]:   'Maker & Retailer',
  [COMPANY_TYPE.AGENCY]:           'Agency',
  [COMPANY_TYPE.SERVICE_PROVIDER]: 'Service Provider/Partnerships',
};

// Max characters shown for text columns before truncating with an ellipsis.
// The full text is revealed via a tooltip on hover.
export const COMPANY_TEXT_MAX_LENGTH = {
  name:         30,
  companyId:    12,
  type:         28,
  taxId:        14,
  commercialOwner: 24,
} as const;

// Sentinel value shown in the Commercial owner column when a company has more than one owner.
// Rendered as a blue link (see .fh-owner-link) instead of a plain email.
export const COMPANY_MULTIPLE_OWNERS = 'Multiple';

// Placeholder shown in table cells when the backend value is empty (e.g. Tax id, Commercial owner).
export const COMPANY_EMPTY_VALUE = '-';

// Permission slugs that grant access to the Companies tab. The tab is visible when the user
// has ANY of these (or is an admin — handled by HasPermissionsService.checkPermission).
export const COMPANY_PERMISSIONS = {
  KAM_CREATOR:      'financial-hub/companies/kam-creator',
  FINANCE_APPROVER: 'financial-hub/companies/finance-approver',
} as const;

// Query param names used on the companies routes (list → detail navigation).
// `country` is read by CountryQueryParamGuard to switch the country context before the detail loads.
export const COMPANY_QUERY_PARAM = {
  COUNTRY: 'country',
  FROM: 'from',
} as const;

export const COMPANY_QUERY_FROM = {
  LIST: 'list',
} as const;

// ── Company lifecycle (HU5) ──────────────────────────────────────────────────

// Action identifiers matching the keys in company-permissions.config.json → "actions".
export const COMPANY_ACTION = {
  EDIT:                        'edit',
  SUBMIT_FOR_VERIFICATION:     'submit_for_verification',
  RETRY_COMPLIANCE_VALIDATION: 'retry_compliance_validation',
  FORCE_FINANCIAL_VALIDATION:  'force_financial_validation',
  VERIFY:                      'verify',
  RETURN_TO_KAM:               'return_to_kam',
  FORCE_VERIFICATION:          'force_verification',
  BLOCK_COMPANY:               'block_company',
  UNBLOCK:                     'unblock',
  COMMENT:                     'comment',
} as const;
export type CompanyAction = typeof COMPANY_ACTION[keyof typeof COMPANY_ACTION];

// Maps the config's role keys (kam_creator, finance_approver) to the COMPANY_PERMISSIONS slugs
// used by HasPermissionsService.checkPermission(). Company has a single lifecycle flow with only
// these two roles (PRD HU5.A) — there is no business_approver as in purchase orders.
export const COMPANY_ROLE_TO_PERMISSION: Record<string, string> = {
  kam_creator:      COMPANY_PERMISSIONS.KAM_CREATOR,
  finance_approver: COMPANY_PERMISSIONS.FINANCE_APPROVER,
};

// Tooltips for the header action buttons in the company detail view (mirrors PO_VIEW_TOOLTIP).
export const COMPANY_VIEW_TOOLTIP = {
  EDIT:    'Edit',
  BLOCK:   'Block company',
  UNBLOCK: 'Unblock company',
} as const;

// Normalizes a backend status (UPPER_CASE, with spaces or dashes) to the snake_case code used by
// COMPANY_STATUS and the permissions config keys. The company detail endpoint returns the status
// in UPPER_CASE (e.g. "AWAITING_FINANCIAL_VALIDATION"), so callers must normalize before any
// config/label lookup or the match fails silently. Mirrors MakersFinancesMgrService's internal
// _normalizeStatusCode so both paths produce the same canonical code.
export function normalizeCompanyStatus(status: string | null | undefined): string | null {
  return status ? status.toLowerCase().replace(/[\s-]+/g, '_') : null;
}

// Success toasts for lifecycle actions (PRD HU5 "Copys").
export const COMPANY_SUCCESS_MESSAGE = {
  SUBMIT:             'Company submitted for verification.',
  VERIFY:             'Company verified.',
  RETURN_TO_KAM:      'Company returned to the KAM.',
  BLOCK:              'Company blocked.',
  UNBLOCK:            'Company unblocked.',
  RETRY_COMPLIANCE:           'Compliance validation requested again.',
  FORCE_VERIFICATION:         'Company verified.',
  FORCE_FINANCIAL_VALIDATION: 'Financial validation forced.',
  COMMENT:                    'Comment added successfully.',
} as const;

// ── Confirmation dialog config (reuses PoActionDialogComponent via inline config) ────────────

// Dialog action types for company lifecycle actions that open a confirmation modal.
export const COMPANY_DIALOG_TYPE = {
  SUBMIT:                     'submit',
  VERIFY:                     'verify',
  RETURN_TO_KAM:              'return_to_kam',
  FORCE_VERIFICATION:         'force_verification',
  FORCE_FINANCIAL_VALIDATION: 'force_financial_validation',
  BLOCK:                      'block',
  UNBLOCK:                    'unblock',
  RETRY_COMPLIANCE:           'retry_compliance',
} as const;
export type CompanyDialogType = typeof COMPANY_DIALOG_TYPE[keyof typeof COMPANY_DIALOG_TYPE];

// Dialog configuration per type. Reuses the PoActionDialogComponent (via its inline `config`
// input): types with a `textAreaPlaceholder` render the textarea mode so the required
// comment / reason / justification can be captured. Copys come from PRD HU5.
export const COMPANY_DIALOG_CONFIG: Record<CompanyDialogType, PoDialogConfig> = {
  [COMPANY_DIALOG_TYPE.SUBMIT]: {
    title:     'Submit for verification?',
    message:   'This company will be sent to Settlement for compliance verification.',
    goBack:    'Go back',
    confirm:   'Yes, submit',
    icon:      PO_DIALOG_ICON.APPROVE,
    iconColor: '#4589FF',
  },
  [COMPANY_DIALOG_TYPE.VERIFY]: {
    title:     'Verify this company?',
    message:   'Once verified, this company will be available to create and approve purchase orders.',
    goBack:    'Cancel',
    confirm:   'Yes, verify',
    icon:      PO_DIALOG_ICON.APPROVE,
    iconColor: '#4589FF',
  },
  [COMPANY_DIALOG_TYPE.RETURN_TO_KAM]: {
    title:              'Return this company to the KAM',
    message:            '',
    goBack:             'Cancel',
    confirm:            'Confirm return',
    icon:               PO_DIALOG_ICON.TRASH,
    iconColor:          '#FF2525',
    textAreaPlaceholder: 'Explain what needs to be corrected. The KAM will see this comment.',
  },
  [COMPANY_DIALOG_TYPE.FORCE_VERIFICATION]: {
    title:              'Verify without re-validating',
    message:            '',
    goBack:             'Cancel',
    confirm:            'Yes, verify',
    icon:               PO_DIALOG_ICON.APPROVE,
    iconColor:          '#4589FF',
    textAreaPlaceholder: 'This company was verified before and its fiscal data changed. Explain why you are verifying it without a new compliance check.',
  },
  [COMPANY_DIALOG_TYPE.FORCE_FINANCIAL_VALIDATION]: {
    title:              'Skip the compliance review',
    message:            '',
    goBack:             'Cancel',
    confirm:            'Confirm',
    icon:               PO_DIALOG_ICON.APPROVE,
    iconColor:          '#4589FF',
    textAreaPlaceholder: 'Compliance has not cleared this company. Explain why you are moving it forward.',
  },
  [COMPANY_DIALOG_TYPE.BLOCK]: {
    title:              'Block this company',
    message:            '',
    goBack:             'Cancel',
    confirm:            'Block company',
    icon:               PO_DIALOG_ICON.LOCK,
    iconColor:          '#FF2525',
    textAreaPlaceholder: 'Blocked companies cannot be used in purchase orders. You can unblock it later.',
  },
  [COMPANY_DIALOG_TYPE.UNBLOCK]: {
    title:     'Unblock this company?',
    message:   'This will restore the company to Verified and allow it to be used in purchase orders again.',
    goBack:    'Go back',
    confirm:   'Unblock',
    icon:      PO_DIALOG_ICON.UNLOCK,
    iconColor: '#4589FF',
  },
  [COMPANY_DIALOG_TYPE.RETRY_COMPLIANCE]: {
    title:     'Retry compliance validation?',
    message:   'This will send the company back to compliance validation.',
    goBack:    'Go back',
    confirm:   'Yes, retry',
    icon:      PO_DIALOG_ICON.APPROVE,
    iconColor: '#4589FF',
  },
};

